import { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Icon } from 'leaflet';
import { Truck, Navigation, Clock, AlertTriangle, Search, X, Map as MapIcon, Cloud } from 'lucide-react';
import { getVehicleLocations } from '../../services/locationService';
import { logSpeedingEvent, getCurrentDriverForTruck } from '../../services/safetyEventService';
import { supabase } from '../../lib/supabase';
import 'leaflet/dist/leaflet.css';

// Speed limit threshold for speeding alerts (in mph)
// Adjust this value based on your fleet's speed policy
const SPEED_LIMIT_MPH = 75;

interface TruckLocation {
  vehicleId: string;
  unitNumber: string;
  latitude: number;
  longitude: number;
  heading?: number;
  speed?: number;
  time: string;
  isActive?: boolean;
  isOffline?: boolean;
  currentLoad?: {
    loadNumber: string;
    pickupLocation: string;
    deliveryLocation: string;
    status: string;
    pickupDate?: string;
    deliveryDate?: string;
  };
}

// Component to handle map centering only when truck is selected from search
function MapController({ selectedTruck, center, zoom, markerRefs }: { selectedTruck: string | null; center: [number, number]; zoom: number; markerRefs: React.MutableRefObject<{ [key: string]: L.Marker }> }) {
  const map = useMap();
  const prevSelectedTruck = useRef<string | null>(null);
  
  useEffect(() => {
    // Only update view when a new truck is selected from search
    if (selectedTruck && selectedTruck !== prevSelectedTruck.current) {
      map.setView(center, zoom);
      prevSelectedTruck.current = selectedTruck;
      
      // Open the popup for the selected truck
      setTimeout(() => {
        const marker = markerRefs.current[selectedTruck];
        if (marker) {
          marker.openPopup();
        }
      }, 100);
    }
  }, [selectedTruck, center, zoom, map, markerRefs]);
  
  return null;
}

export function LiveTruckMap() {
  const [truckLocations, setTruckLocations] = useState<TruckLocation[]>([]);
  const [allTruckLocations, setAllTruckLocations] = useState<TruckLocation[]>([]);
  const [visibleTrucks, setVisibleTrucks] = useState<TruckLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTruck, setSelectedTruck] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([39.8283, -98.5795]);
  const [mapZoom, setMapZoom] = useState(4);
  const [mapType, setMapType] = useState<'street' | 'satellite' | 'weather'>('street');
  const [openWeatherApiKey, setOpenWeatherApiKey] = useState('');
  const [weatherTimestamp, setWeatherTimestamp] = useState<number>(0);
  const [showPrecipitation, setShowPrecipitation] = useState(true);
  const [showClouds, setShowClouds] = useState(true);
  const [showWind, setShowWind] = useState(false);
  const [filterMode, setFilterMode] = useState<'all' | 'my_trucks'>('all');
  const [userProfile, setUserProfile] = useState<any>(null);
  const [myTruckIds, setMyTruckIds] = useState<Set<string>>(new Set());
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const markerRefs = useRef<{ [key: string]: L.Marker }>({});
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousPositions = useRef<{ [key: string]: { lat: number; lng: number } }>({});
  const [displayPositions, setDisplayPositions] = useState<{ [key: string]: { lat: number; lng: number } }>({});
  const animationTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});
  const lastSpeedingLogTime = useRef<{ [key: string]: number }>({});

  useEffect(() => {
    loadUserProfile();
    loadOpenWeatherApiKey();
  }, []);

  // Fetch latest weather radar timestamp when weather mode is selected
  useEffect(() => {
    if (mapType === 'weather') {
      const fetchWeatherTimestamp = async () => {
        try {
          const response = await fetch('https://api.rainviewer.com/public/weather-maps.json');
          const data = await response.json();
          if (data && data.radar && data.radar.past && data.radar.past.length > 0) {
            // Get the most recent radar timestamp
            const latestRadar = data.radar.past[data.radar.past.length - 1];
            setWeatherTimestamp(latestRadar.time);
            console.log('Weather radar timestamp:', latestRadar.time, new Date(latestRadar.time * 1000));
          }
        } catch (error) {
          console.error('Failed to fetch weather radar data:', error);
        }
      };
      fetchWeatherTimestamp();
      // Refresh weather data every 5 minutes
      const interval = setInterval(fetchWeatherTimestamp, 300000);
      return () => clearInterval(interval);
    }
  }, [mapType]);

  // OpenWeatherMap tile keys are client-side by nature — they appear in the map
  // tile URLs the browser loads. Use the configured public key directly rather
  // than reading system_settings (which is admin-only after the RLS lockdown).
  const loadOpenWeatherApiKey = async () => {
    setOpenWeatherApiKey('d5726a172b9d5c3559c363b993379621');
  };

  // Animate markers when positions change
  useEffect(() => {
    const newDisplayPositions = { ...displayPositions };
    
    visibleTrucks.forEach(truck => {
      const newPosition = { lat: truck.latitude, lng: truck.longitude };
      const previousPosition = previousPositions.current[truck.vehicleId];

      // If we have a previous position and it's different, animate the transition
      if (previousPosition && 
          (Math.abs(previousPosition.lat - newPosition.lat) > 0.0001 || 
           Math.abs(previousPosition.lng - newPosition.lng) > 0.0001)) {
        
        // Clear any existing animation timeout
        if (animationTimeouts.current[truck.vehicleId]) {
          clearTimeout(animationTimeouts.current[truck.vehicleId]);
        }

        // Keep the marker at current position during animation
        newDisplayPositions[truck.vehicleId] = previousPosition;
        
        // Get the marker and animate it
        const marker = markerRefs.current[truck.vehicleId];
        if (marker) {
          const currentLatLng = marker.getLatLng();
          animateMarker(marker, currentLatLng, newPosition, 10000); // 10 second animation
          
          // After animation completes, update display position
          animationTimeouts.current[truck.vehicleId] = setTimeout(() => {
            setDisplayPositions(prev => ({
              ...prev,
              [truck.vehicleId]: newPosition
            }));
          }, 10000);
        }
      } else {
        // No animation needed, update position immediately
        newDisplayPositions[truck.vehicleId] = newPosition;
      }

      // Update the previous position
      previousPositions.current[truck.vehicleId] = newPosition;
    });
    
    setDisplayPositions(newDisplayPositions);
  }, [visibleTrucks]);

  useEffect(() => {
    if (userProfile) {
      // Only show loading overlay on initial load or filter change (not on automatic refresh)
      const isInitialLoad = allTruckLocations.length === 0;
      loadTruckLocations(isInitialLoad);
      
      // Refresh every 30 seconds for smooth live updates (no loading overlay)
      const interval = setInterval(() => loadTruckLocations(false), 30000);
      return () => {
        clearInterval(interval);
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
        // Clear all animation timeouts
        Object.values(animationTimeouts.current).forEach(timeout => clearTimeout(timeout));
      };
    }
  }, [userProfile, filterMode]);

  async function loadUserProfile() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profileData } = await supabase
        .from('profiles')
        .select('id, role')
        .eq('id', user.id)
        .single();
      
      setUserProfile(profileData);

      // If user is Dispatch or Dispatch Manager, load their assigned trucks
      if (profileData?.role === 'Dispatch' || profileData?.role === 'Dispatch Manager') {
        const { data: trucksData } = await supabase
          .from('trucks')
          .select('id, unit_number')
          .eq('dispatcher_id', user.id);
        
        console.log('User ID:', user.id);
        console.log('Trucks assigned to this dispatcher:', trucksData);
        
        const truckIds = new Set(trucksData?.map(t => t.id) || []);
        setMyTruckIds(truckIds);
        console.log('My truck IDs:', truckIds);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }

  // Show all trucks immediately - no progressive loading
  useEffect(() => {
    if (allTruckLocations.length === 0) return;
    
    // Show all trucks at once for smooth updates
    setVisibleTrucks(allTruckLocations);
  }, [allTruckLocations]);

  async function loadTruckLocations(showLoadingOverlay = false) {
    try {
      // Show loading spinner on initial load or when explicitly requested (filter change)
      if (allTruckLocations.length === 0) {
        setLoading(true);
      } else if (showLoadingOverlay) {
        setIsFilterLoading(true);
      }
      setError(null);

      // Get locations from Samsara
      const result = await getVehicleLocations();
      
      console.log('Samsara locations result:', result);
      
      if (!result.success || !result.locations) {
        setError(result.error || 'Failed to fetch truck locations');
        setLoading(false);
        return;
      }

      console.log('Received locations from ELD:', result.locations.length);
      console.log('First ELD location full data:', result.locations[0]);

      // Get ALL truck details from database
      const { data: trucks } = await supabase
        .from('trucks')
        .select('id, samsara_id, samsara_vehicle_id, unit_number, dispatcher_id');

      console.log('Fleet trucks from database:', trucks?.length, `(${trucks?.length})`);
      console.log('Sample truck unit_numbers:', trucks?.slice(0, 5).map(t => `"${t.unit_number}"`));
      console.log('Sample ELD data:', result.locations?.slice(0, 3).map(l => ({ vehicleId: l.vehicleId, name: (l as any).name })));

      // Track which trucks have location data
      const trucksWithLocation = new Set();
      
      // Combine location data with truck info - use same matching logic as ELDLocationView
      const locationsWithTrucks = result.locations
        .filter(loc => {
          const hasValidCoords = loc.latitude !== undefined && 
            loc.longitude !== undefined &&
            !isNaN(loc.latitude) &&
            !isNaN(loc.longitude);
          
          if (!hasValidCoords) {
            console.log('Invalid coordinates for vehicleId:', loc.vehicleId, loc);
          }
          
          return hasValidCoords;
        })
        .map(loc => {
          // Match by unit_number with the name field from ProLogs (e.g., "105", "312")
          const locName = (loc as any).name;
          const truck = trucks?.find(t => 
            t.unit_number === locName ||
            t.unit_number === loc.vehicleId?.toString()
          );
          
          if (!truck) {
            console.log('No truck match for vehicleId:', loc.vehicleId, 'name:', locName);
            return null;
          }
          
          console.log('✅ Matched truck:', truck.unit_number, 'with ProLogs name:', locName || loc.vehicleId);
          trucksWithLocation.add(truck.id);
          
          // Determine if truck is active based on recent movement
          const locationTime = new Date(loc.time);
          const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000);
          const isActive = (loc.speed && loc.speed > 0) || locationTime > twoHoursAgo;
          
          return {
            ...loc,
            vehicleId: loc.vehicleId,
            unitNumber: truck.unit_number,
            isActive,
          };
        })
        .filter((loc): loc is NonNullable<typeof loc> => loc !== null);
      
      // Add trucks without location data (offline/in shop)
      // Show them at company headquarters location
      const offlineTrucks = trucks
        ?.filter(t => !trucksWithLocation.has(t.id))
        .map(t => {
          return {
            vehicleId: t.samsara_vehicle_id || t.samsara_id || t.id,
            unitNumber: t.unit_number,
            latitude: 39.8283,
            longitude: -98.5795,
            speed: 0,
            time: new Date().toISOString(),
            isActive: false,
            isOffline: true,
          };
        }) || [];
      
      // Combine trucks with location data and offline trucks
      let allTrucksWithStatus = [...locationsWithTrucks, ...offlineTrucks];
      
      // Deduplicate trucks by unitNumber (multiple ELD connections may return same truck)
      // Keep the most recent location for each truck
      const trucksByUnit = new Map();
      allTrucksWithStatus.forEach(truck => {
        if (!truck.unitNumber) {
          trucksByUnit.set(`no-unit-${Math.random()}`, truck);
          return;
        }
        
        const existing = trucksByUnit.get(truck.unitNumber);
        if (!existing) {
          trucksByUnit.set(truck.unitNumber, truck);
        } else {
          // Keep the one with more recent timestamp
          const existingTime = new Date(existing.time || 0).getTime();
          const newTime = new Date(truck.time || 0).getTime();
          if (newTime > existingTime) {
            trucksByUnit.set(truck.unitNumber, truck);
          }
        }
      });
      
      allTrucksWithStatus = Array.from(trucksByUnit.values());
      
      // Filter trucks based on user role and filter mode
      if (filterMode === 'my_trucks' && (userProfile?.role === 'Dispatch' || userProfile?.role === 'Dispatch Manager')) {
        // Get truck IDs that belong to this dispatcher
        const myTruckUnitNumbers = new Set(
          trucks?.filter(t => myTruckIds.has(t.id)).map(t => t.unit_number) || []
        );
        console.log('Filter mode: my_trucks');
        console.log('My truck unit numbers:', Array.from(myTruckUnitNumbers));
        console.log('Before filtering:', allTrucksWithStatus.length, 'trucks');
        
        allTrucksWithStatus = allTrucksWithStatus.filter(truck => 
          myTruckUnitNumbers.has(truck.unitNumber)
        );
        
        console.log('After filtering:', allTrucksWithStatus.length, 'trucks');
      }
      
      console.log('Trucks with location:', locationsWithTrucks.length);
      console.log('Offline trucks:', offlineTrucks.length);
      console.log('Total trucks on map:', allTrucksWithStatus.length);

      // Fetch current loads for all trucks
      const truckIds = trucks?.map(t => t.id) || [];
      const { data: currentLoads } = await supabase
        .from('dispatches')
        .select(`
          truck_id,
          loads!inner(
            load_number,
            pickup_city,
            pickup_state,
            delivery_city,
            delivery_state,
            pickup_date,
            delivery_date,
            status
          )
        `)
        .in('truck_id', truckIds)
        .in('loads.status', ['BOOKED', 'DISPATCHED', 'LOADED', 'booked', 'dispatched', 'loaded']);

      // Create a map of truck_id to current load
      const loadMap = new Map();
      currentLoads?.forEach(dispatch => {
        const load = dispatch.loads as any;
        if (load && !loadMap.has(dispatch.truck_id)) {
          loadMap.set(dispatch.truck_id, {
            loadNumber: load.load_number,
            pickupLocation: `${load.pickup_city || ''}, ${load.pickup_state || ''}`.trim(),
            deliveryLocation: `${load.delivery_city || ''}, ${load.delivery_state || ''}`.trim(),
            status: load.status,
            pickupDate: load.pickup_date,
            deliveryDate: load.delivery_date,
          });
        }
      });

      // Add load information to trucks
      allTrucksWithStatus = allTrucksWithStatus.map(truck => {
        const truckData = trucks?.find(t => t.unit_number === truck.unitNumber);
        const currentLoad = truckData ? loadMap.get(truckData.id) : null;
        return {
          ...truck,
          currentLoad: currentLoad || undefined,
        };
      });

      // Always update state to trigger re-render
      setAllTruckLocations(allTrucksWithStatus);
      setTruckLocations(allTrucksWithStatus);

      // Check for speeding trucks and log events
      await checkAndLogSpeedingEvents(allTrucksWithStatus, trucks || []);
    } catch (err) {
      console.error('Error loading truck locations:', err);
      setError('Failed to load truck locations');
    } finally {
      setLoading(false);
      setIsFilterLoading(false);
    }
  }

  // Function to check for speeding trucks and log events to database
  async function checkAndLogSpeedingEvents(trucks: TruckLocation[], truckData: any[]) {
    const now = Date.now();
    const THIRTY_MINUTES = 30 * 60 * 1000; // 30 minutes in milliseconds

    console.log(`🔍 Checking ${trucks.length} trucks for speeding (limit: ${SPEED_LIMIT_MPH} mph)...`);
    
    const speedingTrucks = trucks.filter(t => !t.isOffline && (t.speed ?? 0) > SPEED_LIMIT_MPH);
    console.log(`⚠️ Found ${speedingTrucks.length} trucks speeding:`, speedingTrucks.map(t => `${t.unitNumber} (${t.speed} mph)`));

    for (const truck of trucks) {
      // Skip offline trucks
      if (truck.isOffline) continue;

      // Check if truck is speeding
      const isSpeeding = (truck.speed ?? 0) > SPEED_LIMIT_MPH;
      
      if (isSpeeding) {
        console.log(`🚨 Truck ${truck.unitNumber} is speeding: ${truck.speed} mph (limit: ${SPEED_LIMIT_MPH} mph)`);
        
        // Check if we've logged this truck recently (within 30 minutes)
        const lastLogTime = lastSpeedingLogTime.current[truck.vehicleId];
        if (lastLogTime && (now - lastLogTime) < THIRTY_MINUTES) {
          console.log(`⏭️ Skipping ${truck.unitNumber} - already logged within 30 minutes`);
          continue; // Skip logging, too recent
        }

        // Find the truck ID from the database
        const truckRecord = truckData.find(t => 
          t.samsara_vehicle_id === truck.vehicleId || t.samsara_id === truck.vehicleId
        );

        if (!truckRecord) {
          console.warn(`No truck record found for vehicle ID: ${truck.vehicleId}`);
          continue;
        }

        // Get the current driver for this truck
        const driverInfo = await getCurrentDriverForTruck(truckRecord.id);

        // Log the speeding event
        const result = await logSpeedingEvent({
          truckId: truckRecord.id,
          samsaraVehicleId: truck.vehicleId,
          unitNumber: truck.unitNumber,
          speedMph: Math.round(truck.speed ?? 0),
          speedLimitMph: SPEED_LIMIT_MPH,
          latitude: truck.latitude,
          longitude: truck.longitude,
          eventTime: truck.time,
          driverId: driverInfo.driverId,
          driverName: driverInfo.driverName
        });

        if (result.success) {
          // Update the last log time for this truck
          lastSpeedingLogTime.current[truck.vehicleId] = now;
          console.log(`🚨 Speeding event logged: ${truck.unitNumber} - ${truck.speed} mph (Driver: ${driverInfo.driverName || 'Unknown'})`);
        }
      }
    }
  }

  // Function to smoothly animate marker from one position to another
  const animateMarker = (marker: L.Marker, startLatLng: L.LatLng, endLatLng: { lat: number; lng: number }, duration: number) => {
    const startTime = Date.now();
    const startLat = startLatLng.lat;
    const startLng = startLatLng.lng;
    const latDiff = endLatLng.lat - startLat;
    const lngDiff = endLatLng.lng - startLng;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation (ease-in-out)
      const easeProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      const currentLat = startLat + latDiff * easeProgress;
      const currentLng = startLng + lngDiff * easeProgress;

      marker.setLatLng([currentLat, currentLng]);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  };

  // Function to create colored pulsing dot icon
  const createTruckIcon = (isActive: boolean, isSpeeding: boolean = false) => {
    // Red for speeding, green for active, gray for inactive
    const color = isSpeeding ? '#ef4444' : (isActive ? '#10b981' : '#6b7280');
    const darkColor = isSpeeding ? '#dc2626' : (isActive ? '#059669' : '#4b5563');
    
    return new Icon({
      iconUrl: 'data:image/svg+xml;base64,' + btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
          <defs>
            <radialGradient id="dotGradient-${isActive ? 'active' : 'inactive'}">
              <stop offset="0%" style="stop-color:${color};stop-opacity:1" />
              <stop offset="100%" style="stop-color:${darkColor};stop-opacity:1" />
            </radialGradient>
          </defs>
          
          <!-- Outer pulsing ring -->
          <circle cx="20" cy="20" r="16" fill="${color}" opacity="0.2">
            <animate attributeName="r" values="12;18;12" dur="2s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.4;0.1;0.4" dur="2s" repeatCount="indefinite"/>
          </circle>
          
          <!-- Middle ring -->
          <circle cx="20" cy="20" r="10" fill="${color}" opacity="0.4">
            <animate attributeName="r" values="8;12;8" dur="2s" repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2s" repeatCount="indefinite"/>
          </circle>
          
          <!-- Core dot -->
          <circle cx="20" cy="20" r="6" fill="url(#dotGradient-${isActive ? 'active' : 'inactive'})">
            <animate attributeName="r" values="5;7;5" dur="2s" repeatCount="indefinite"/>
          </circle>
          
          <!-- White center -->
          <circle cx="20" cy="20" r="3" fill="white" opacity="0.9"/>
        </svg>
      `),
      iconSize: [40, 40],
      iconAnchor: [20, 20],
      popupAnchor: [0, -20],
    });
  };

  // Filter trucks based on search query (use all trucks for search, not just visible)
  const filteredTrucks = truckLocations.filter(truck =>
    truck.unitNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Use visible trucks for rendering on map
  const trucksToRender = visibleTrucks.length > 0 ? visibleTrucks : truckLocations;

  // Handle truck selection from search
  const handleTruckSelect = (truck: TruckLocation) => {
    setSelectedTruck(truck.vehicleId);
    setMapCenter([truck.latitude, truck.longitude]);
    setMapZoom(15);
    setSearchQuery('');
  };

  // Initial center - only used when map first loads
  const defaultCenter: [number, number] = [39.8283, -98.5795]; // Center of USA

  // Show loading only on first load, not during progressive rendering
  if (loading && truckLocations.length === 0 && visibleTrucks.length === 0) {
    return (
      <div className="bg-slate-100 rounded-lg h-64 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-slate-500 text-sm">Loading truck locations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg h-64 flex items-center justify-center">
        <div className="text-center text-red-600">
          <p className="font-semibold">Failed to load truck locations</p>
          <p className="text-sm">{error}</p>
          <p className="text-xs mt-2 text-slate-500">
            Configure Samsara API in Settings → Integrations
          </p>
        </div>
      </div>
    );
  }

  // If no truck locations and not loading, show empty state
  if (truckLocations.length === 0 && !loading) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg h-64 flex items-center justify-center">
        <div className="text-center text-slate-500">
          <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="font-semibold">No truck locations available</p>
          <p className="text-sm">Configure Samsara integration in Settings</p>
        </div>
      </div>
    );
  }

  try {
    return (
      <div className="relative rounded-lg overflow-hidden h-[500px]">
        {/* Search Bar */}
        <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-10 w-80">
          <div className="relative">
            <div className="flex items-center bg-white rounded-lg shadow-lg">
              <Search className="w-4 h-4 text-slate-400 ml-3" />
              <input
                type="text"
                placeholder="Search truck by unit number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 text-sm focus:outline-none rounded-lg"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mr-2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Search Results Dropdown */}
            {searchQuery && filteredTrucks.length > 0 && (
              <div className="absolute top-full mt-1 w-full bg-white rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredTrucks.map((truck) => {
                  const isSpeeding = (truck.speed ?? 0) > SPEED_LIMIT_MPH;
                  return (
                    <button
                      key={truck.unitNumber || truck.vehicleId}
                      onClick={() => handleTruckSelect(truck)}
                      className="w-full px-4 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          isSpeeding ? 'bg-red-500 animate-pulse' : 
                          truck.isActive ? 'bg-green-500' : 'bg-gray-500'
                        }`}></div>
                        <span className="font-semibold text-slate-900">{truck.unitNumber}</span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {truck.speed !== undefined ? `${Math.round(truck.speed)} mph` : 'No speed'}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            
            {/* No Results */}
            {searchQuery && filteredTrucks.length === 0 && (
              <div className="absolute top-full mt-1 w-full bg-white rounded-lg shadow-lg px-4 py-3 text-sm text-slate-500 text-center">
                No trucks found matching "{searchQuery}"
              </div>
            )}
          </div>
        </div>

        <MapContainer
          center={defaultCenter}
          zoom={4}
          style={{ height: '100%', width: '100%' }}
          className="z-0"
          maxBounds={[[-90, -180], [90, 180]]}
          maxBoundsViscosity={1.0}
          minZoom={2}
          attributionControl={false}
        >
          <MapController selectedTruck={selectedTruck} center={mapCenter} zoom={mapZoom} markerRefs={markerRefs} />
          
          {mapType === 'street' ? (
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              noWrap={true}
              errorTileUrl="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
            />
          ) : mapType === 'satellite' ? (
            <>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                noWrap={true}
                errorTileUrl="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                noWrap={true}
                errorTileUrl="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
              />
            </>
          ) : (
            <>
              {/* Regular street map base for weather view */}
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                noWrap={true}
                errorTileUrl="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
              />
              {/* NOAA Weather Radar - Precipitation (Rain & Snow) */}
              {showPrecipitation && (
                <TileLayer
                  url="https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/nexrad-n0q-900913/{z}/{x}/{y}.png"
                  opacity={0.6}
                />
              )}
              {/* RainViewer Global Radar as backup */}
              {showPrecipitation && weatherTimestamp > 0 && (
                <TileLayer
                  key={`radar-${weatherTimestamp}`}
                  url={`https://tilecache.rainviewer.com/v2/radar/${weatherTimestamp}/{z}/{x}/{y}/256/1_1.png`}
                  opacity={0.5}
                />
              )}
              {/* Satellite Clouds Layer - Shows actual cloud coverage */}
              {showClouds && (
                <TileLayer
                  url={`https://tile.openweathermap.org/map/clouds_new/{z}/{x}/{y}.png?appid=d5726a172b9d5c3559c363b993379621`}
                  opacity={0.8}
                  className="cloud-layer-dark"
                />
              )}
              {/* Temperature Layer as Wind indicator */}
              {showWind && (
                <TileLayer
                  url={`https://tile.openweathermap.org/map/temp_new/{z}/{x}/{y}.png?appid=d5726a172b9d5c3559c363b993379621`}
                  opacity={0.4}
                />
              )}
            </>
          )}
        
        {trucksToRender.map((truck) => {
          const isSpeeding = (truck.speed ?? 0) > SPEED_LIMIT_MPH;
          // Use display position if available (during animation), otherwise use actual position
          const displayPos = displayPositions[truck.vehicleId];
          const position: [number, number] = displayPos 
            ? [displayPos.lat, displayPos.lng]
            : [truck.latitude, truck.longitude];
          
          return (
          <Marker
            key={truck.unitNumber || truck.vehicleId}
            position={position}
            icon={createTruckIcon(truck.isActive ?? true, isSpeeding)}
            ref={(ref) => {
              if (ref) {
                markerRefs.current[truck.vehicleId] = ref as any;
              }
            }}
          >
            <Popup>
              <div className="p-2 min-w-[200px]">
                <div className="flex items-center gap-2 mb-2">
                  <Truck className={`w-4 h-4 ${truck.isActive ? 'text-green-600' : 'text-red-600'}`} />
                  <span className="font-bold text-slate-900">{truck.unitNumber}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    truck.isActive 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {truck.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                {isSpeeding && (
                  <div className="flex items-center gap-2 mb-2 px-2 py-1 bg-red-100 border border-red-300 rounded animate-pulse">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    <span className="text-xs font-bold text-red-700">SPEEDING ALERT</span>
                  </div>
                )}
                
                {truck.speed !== undefined && (
                  <div className={`flex items-center gap-2 text-sm mb-1 ${
                    isSpeeding ? 'text-red-600 font-bold' : 'text-slate-600'
                  }`}>
                    <Navigation className="w-3 h-3" />
                    <span>{Math.round(truck.speed)} mph</span>
                    {isSpeeding && <span className="text-xs">(OVER LIMIT)</span>}
                  </div>
                )}
                
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Clock className="w-3 h-3" />
                  <span>{new Date(truck.time).toLocaleString()}</span>
                </div>
                
                {/* Current Load Information */}
                {truck.currentLoad && (
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    <div className="text-xs font-semibold text-slate-700 mb-1">Current Load</div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-slate-600">Load #:</span>
                        <span className="text-xs text-slate-900 font-semibold">{truck.currentLoad.loadNumber}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-slate-600">Status:</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          truck.currentLoad.status.toUpperCase() === 'DISPATCHED' 
                            ? 'bg-blue-100 text-blue-700'
                            : truck.currentLoad.status.toUpperCase() === 'LOADED'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {truck.currentLoad.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-xs">
                        <div className="flex items-start gap-1 mb-1">
                          <span className="text-slate-600 font-medium">Pickup:</span>
                          <span className="text-slate-900">{truck.currentLoad.pickupLocation}</span>
                        </div>
                        {truck.currentLoad.pickupDate && (
                          <div className="text-slate-500 ml-12 text-[10px]">
                            {new Date(truck.currentLoad.pickupDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <div className="text-xs">
                        <div className="flex items-start gap-1 mb-1">
                          <span className="text-slate-600 font-medium">Delivery:</span>
                          <span className="text-slate-900">{truck.currentLoad.deliveryLocation}</span>
                        </div>
                        {truck.currentLoad.deliveryDate && (
                          <div className="text-slate-500 ml-12 text-[10px]">
                            {new Date(truck.currentLoad.deliveryDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {!truck.currentLoad && (
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    <div className="text-xs text-slate-500 italic">No active load</div>
                  </div>
                )}
                
                <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-500">
                  <div>Lat: {truck.latitude?.toFixed(6) || 'N/A'}</div>
                  <div>Lng: {truck.longitude?.toFixed(6) || 'N/A'}</div>
                </div>
              </div>
            </Popup>
          </Marker>
          );
        })}
      </MapContainer>
      
      {/* Filter Toggle for Dispatch/Dispatch Manager */}
      {(userProfile?.role === 'Dispatch' || userProfile?.role === 'Dispatch Manager') && myTruckIds.size > 0 && (
        <div className="absolute bottom-2 right-2 bg-white rounded-lg shadow-md z-[1000] overflow-hidden">
          <button
            onClick={() => setFilterMode('all')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              filterMode === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              <span>All Trucks</span>
            </div>
          </button>
          <button
            onClick={() => setFilterMode('my_trucks')}
            className={`px-4 py-2 text-sm font-medium transition-colors border-l border-slate-200 ${
              filterMode === 'my_trucks'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              <span>My Trucks</span>
            </div>
          </button>
        </div>
      )}
      
      {/* Map Type Toggle */}
      <div className="absolute top-2 right-2 bg-white rounded-lg shadow-md z-[1000] overflow-hidden">
        <button
          onClick={() => setMapType('street')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            mapType === 'street'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          <div className="flex items-center gap-2">
            <MapIcon className="w-4 h-4" />
            <span>Street</span>
          </div>
        </button>
        <button
          onClick={() => setMapType('satellite')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-l border-slate-200 ${
            mapType === 'satellite'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          <div className="flex items-center gap-2">
            <MapIcon className="w-4 h-4" />
            <span>Satellite</span>
          </div>
        </button>
        <button
          onClick={() => setMapType('weather')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-l border-slate-200 ${
            mapType === 'weather'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          <div className="flex items-center gap-2">
            <Cloud className="w-4 h-4" />
            <span>Weather</span>
          </div>
        </button>
      </div>

      {/* Weather Layer Controls - Only show when in weather mode */}
      {mapType === 'weather' && (
        <div className="absolute top-16 right-2 bg-white rounded-lg shadow-md z-[1000] p-3">
          <div className="text-xs font-bold text-slate-700 mb-2">WEATHER LAYERS</div>
          <div className="space-y-2 mb-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showPrecipitation}
                onChange={(e) => setShowPrecipitation(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700">Rain/Snow</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showClouds}
                onChange={(e) => setShowClouds(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700">Clouds</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showWind}
                onChange={(e) => setShowWind(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700">Temperature</span>
            </label>
          </div>
          
          {/* Weather Color Legend */}
          <div className="border-t border-slate-200 pt-3">
            <div className="text-xs font-bold text-slate-700 mb-2">COLOR LEGEND</div>
            <div className="space-y-1.5">
              {showPrecipitation && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-3 rounded" style={{ backgroundColor: '#40ff40' }}></div>
                    <span className="text-xs text-slate-600">Light Rain</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-3 rounded" style={{ backgroundColor: '#ffff40' }}></div>
                    <span className="text-xs text-slate-600">Moderate Rain</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-3 rounded" style={{ backgroundColor: '#ff8c00' }}></div>
                    <span className="text-xs text-slate-600">Heavy Rain</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-3 rounded" style={{ backgroundColor: '#ff0000' }}></div>
                    <span className="text-xs text-slate-600">Severe Storm</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-3 rounded" style={{ backgroundColor: '#ffffff', border: '1px solid #ccc' }}></div>
                    <span className="text-xs text-slate-600">Snow</span>
                  </div>
                </>
              )}
              {showWind && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-3 rounded" style={{ backgroundColor: '#0000ff' }}></div>
                    <span className="text-xs text-slate-600">Cold Air</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-3 rounded" style={{ backgroundColor: '#ff0000' }}></div>
                    <span className="text-xs text-slate-600">Warm Air</span>
                  </div>
                </>
              )}
              {showClouds && (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-3 rounded" style={{ backgroundColor: '#e0e0e0' }}></div>
                  <span className="text-xs text-slate-600">Cloud Cover</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Legend */}
      <div className="absolute top-2 left-2 bg-white px-4 py-3 rounded-lg shadow-md z-[1000]">
        <div className="text-xs font-bold text-slate-700 mb-2">TRUCK STATUS</div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
            <span className="text-xs text-slate-700">Speeding (&gt;{SPEED_LIMIT_MPH} mph)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-xs text-slate-700">Active (Moving)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-500"></div>
            <span className="text-xs text-slate-700">Inactive (Stopped)</span>
          </div>
        </div>
      </div>
      
      {/* Filter loading indicator */}
      {isFilterLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 z-[999] flex items-center justify-center">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-sm font-medium text-slate-700">Updating map...</span>
            </div>
          </div>
        </div>
      )}
      
      
      {/* Truck count badge */}
      <div className="absolute bottom-2 left-2 bg-white px-3 py-1.5 rounded-full shadow-md z-[1000] flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-green-500"></div>
          <span className="text-sm font-semibold text-slate-900">
            {trucksToRender.filter(t => t.isActive && (t.speed ?? 0) <= SPEED_LIMIT_MPH).length} Active
          </span>
        </div>
        <div className="w-px h-4 bg-slate-300"></div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
          <span className="text-sm font-semibold text-slate-900">
            {trucksToRender.filter(t => (t.speed ?? 0) > SPEED_LIMIT_MPH).length} Speeding
          </span>
        </div>
        <div className="w-px h-4 bg-slate-300"></div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-gray-500"></div>
          <span className="text-sm font-semibold text-slate-900">
            {trucksToRender.filter(t => !t.isActive).length} Inactive
          </span>
        </div>
      </div>
      
      {/* No trucks overlay */}
      {truckLocations.length === 0 && !loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-[1000] pointer-events-none">
          <div className="text-center text-slate-500">
            <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="font-semibold">No active trucks</p>
            <p className="text-sm">Truck locations will appear here when available</p>
          </div>
        </div>
      )}
    </div>
    );
  } catch (mapError) {
    console.error('Map rendering error:', mapError);
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg h-64 flex items-center justify-center">
        <div className="text-center text-red-600">
          <p className="font-semibold">Map failed to render</p>
          <p className="text-sm">Please refresh the page</p>
        </div>
      </div>
    );
  }
}
